"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  defaultPerks,
  games,
  isOverwolf,
  TEBEX_ENABLED,
  TH_GL_URL,
  useAccountStore,
} from "@repo/lib";
import { Badge, Button } from "../(controls)";
import { Separator } from "../ui/separator";
import Cookies from "js-cookie";
import { ExternalAnchor } from "./external-anchor";
import { useUnlockWithSecret } from "./use-unlock-with-secret";
import { EmailSignIn } from "./email-sign-in";
import { useZh } from "./prefers-chinese";
import { Input } from "../ui/input";
import { useMemo, useState } from "react";
import {
  ExternalLink,
  LogOut,
  Shield,
  Star,
  Eye,
  Ticket,
  Zap,
} from "lucide-react";
import { toSvg } from "jdenticon";

const PERK_CONFIG = [
  {
    key: "adRemoval" as const,
    label: "Ad-Free",
    icon: Shield,
    tier: "Pro+",
  },
  {
    key: "premiumFeatures" as const,
    label: "Premium",
    icon: Zap,
    tier: "Pro+",
  },
  {
    key: "previewReleaseAccess" as const,
    label: "Preview Access",
    icon: Eye,
    tier: "Elite",
  },
];

function AuthenticatedView() {
  const account = useAccountStore();
  const displayName = account.username || "User";

  const avatarSVG = useMemo(
    () =>
      account.avatarUrl || !account.decryptedUserId
        ? null
        : toSvg(account.decryptedUserId, 48),
    [account.avatarUrl, account.decryptedUserId],
  );

  const activePerks = PERK_CONFIG.filter((p) => account.perks[p.key]);
  // Invite-only companions this account is invited to (games.ts titles).
  const inviteTitles = account.invites.map(
    (id) => games.find((game) => game.id === id)?.title ?? id,
  );
  const hasPerks = activePerks.length > 0 || inviteTitles.length > 0;

  // isSpecial is server-resolved (PATREON_SPECIAL_USERS) and rides on the
  // account payload — perks alone can't distinguish Special from Elite. Every
  // paid tier includes comments; an invited non-supporter has the invite perks
  // (preview/premium) but no comments, so that combination reads "Invited".
  const currentTier = account.isSpecial
    ? "Special"
    : !account.perks.comments && inviteTitles.length > 0
      ? "Invited"
      : account.perks.previewReleaseAccess
        ? "Elite"
        : account.perks.adRemoval
          ? "Pro"
          : account.perks.comments
            ? "Enthusiast"
            : null;

  return (
    <div className="space-y-4">
      {/* Profile */}
      <div className="flex items-center gap-3">
        <div className="shrink-0">
          {account.avatarUrl ? (
            <img
              src={account.avatarUrl}
              alt={displayName}
              className="w-12 h-12 rounded-full object-cover ring-2 ring-primary/30"
            />
          ) : avatarSVG ? (
            <div
              className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-primary/30"
              dangerouslySetInnerHTML={{ __html: avatarSVG }}
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center ring-2 ring-primary/30">
              <Star className="w-5 h-5 text-primary" />
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{displayName}</p>
          {currentTier ? (
            <p className="text-xs text-primary">{currentTier}</p>
          ) : (
            <p className="text-xs text-muted-foreground">No active tier</p>
          )}
          {/* Which Patreon account this actually is. The display name
              above is the TH.GL profile name, not the Patreon identity —
              without this a user with two Patreon accounts cannot tell
              which one the app signed in with. `email` is only present
              when the token carries the identity[email] scope; the id is
              always there and is what support compares against. */}
          {account.email && (
            <p className="text-xs text-muted-foreground truncate">
              {account.email}
            </p>
          )}
          {account.decryptedUserId && (
            <p className="text-[10px] text-muted-foreground/70 truncate">
              {account.decryptedUserId.startsWith("tebex:")
                ? "Account ID"
                : "Patreon ID"}
              : {account.decryptedUserId}
            </p>
          )}
        </div>
      </div>

      {/* Invited: invite-only companions this account may open. Hidden when
          there are none. */}
      {inviteTitles.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Invited
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {inviteTitles.map((title) => (
              <div
                key={title}
                className="flex items-center gap-2 text-xs text-foreground py-1"
              >
                <Ticket className="w-3.5 h-3.5 text-primary shrink-0" />
                <span>{title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Perks */}
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-1.5">
          {PERK_CONFIG.map((perk) => {
            const active = account.perks[perk.key];
            return (
              <div
                key={perk.key}
                className={
                  active
                    ? "flex items-center gap-2 text-xs text-foreground py-1"
                    : "flex items-center gap-2 text-xs text-muted-foreground/40 py-1"
                }
              >
                <perk.icon
                  className={
                    active
                      ? "w-3.5 h-3.5 text-primary shrink-0"
                      : "w-3.5 h-3.5 shrink-0"
                  }
                />
                <span>{perk.label}</span>
                <span className="text-[10px] text-muted-foreground/50 ml-auto">
                  {perk.tier}
                </span>
              </div>
            );
          })}
        </div>
        {!hasPerks && (
          <ExternalAnchor
            href={`${TH_GL_URL}/support-me`}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Star className="w-3 h-3" />
            View tiers & pricing
            <ExternalLink className="w-3 h-3" />
          </ExternalAnchor>
        )}
      </div>

      <Separator />

      {/* Actions */}
      <div className="flex items-center justify-between">
        <ExternalAnchor
          href={`${TH_GL_URL}/support-me/account`}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          Manage profile
          <ExternalLink className="w-3 h-3" />
        </ExternalAnchor>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => {
            Cookies.remove("userId");
            account.setAccount({
              userId: null,
              decryptedUserId: null,
              email: null,
              perks: defaultPerks,
              username: null,
              avatarUrl: null,
            });
          }}
        >
          <LogOut className="w-3 h-3 mr-1" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}

function UnauthenticatedView() {
  const zh = useZh();
  const [userId, setUserId] = useState("");
  const { unlock, loading } = useUnlockWithSecret();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void unlock(userId);
  };

  // Production: round-trip through www.th.gl's authorize route (which
  // supports the return_to state param). Dev: stay on the current
  // tenant so the cookie ends up host-scoped and signed-in state
  // actually applies to whatever subdomain the user was browsing.
  // The /authenticate middleware handler is open to any tenant in dev
  // (see middleware.ts).
  const returnTo = typeof window !== "undefined" ? window.location.href : "";
  const authUrl =
    process.env.NODE_ENV === "development" && typeof window !== "undefined"
      ? `/authenticate?return_to=${encodeURIComponent(returnTo)}`
      : `${TH_GL_URL}/api/patreon/authorize?return_to=${encodeURIComponent(returnTo)}`;

  return (
    <div className="space-y-4">
      {/* Value prop */}
      <p className="text-sm text-muted-foreground">
        Support the project to unlock features across all TH.GL apps.
      </p>

      {/* Perks list */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Perks
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {PERK_CONFIG.map((perk) => (
            <div
              key={perk.key}
              className="flex items-center gap-2 text-xs text-muted-foreground py-1"
            >
              <perk.icon className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>{perk.label}</span>
              <span className="text-[10px] text-muted-foreground/50 ml-auto">
                {perk.tier}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* New user: see tiers & pricing */}
      <ExternalAnchor
        href={`${TH_GL_URL}/support-me`}
        className="flex items-center justify-center gap-1.5 w-full text-sm font-medium text-primary hover:underline"
      >
        <Star className="w-3.5 h-3.5" />
        View tiers & pricing
        <ExternalLink className="w-3 h-3" />
      </ExternalAnchor>

      {/* Existing user: sign in - Patreon, or E-Mail for Tebex (Alipay /
          WeChat Pay) supporters (one-time code, active Tebex purchase only). */}
      <Button className="w-full" asChild>
        <a href={authUrl}>
          {zh ? "使用 Patreon 登录" : "Sign in with Patreon"}
        </a>
      </Button>
      {!isOverwolf && TEBEX_ENABLED && (
        <details className="group">
          <summary className="list-none [&::-webkit-details-marker]:hidden">
            <span className="flex h-9 w-full cursor-pointer items-center justify-center rounded-md border border-input text-sm font-medium hover:bg-accent">
              {zh ? "使用邮箱登录" : "Sign in with E-Mail"}
            </span>
          </summary>
          <EmailSignIn className="mt-2" />
        </details>
      )}

      {/* Account Key (web + Companion App): accounts without a Patreon login
          (Tebex purchases) restore their perks with the key from their account
          page. Stored like a pasted Overwolf secret; the userId cookie is then
          restored from it by the session self-heal (restoreUserIdCookie).
          Collapsed and provider-neutral on purpose (soft launch). */}
      {!isOverwolf && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            {zh ? "有账户密钥？" : "Have an Account Key?"}
          </summary>
          <form onSubmit={handleSubmit} className="flex gap-2 mt-2">
            <Input
              value={userId}
              onChange={(e) => setUserId(e.target.value.trim())}
              placeholder={zh ? "粘贴你的账户密钥" : "Paste your Account Key"}
              className="text-xs h-8"
            />
            <Button
              type="submit"
              size="sm"
              className="h-8 shrink-0"
              disabled={userId.length === 0 || loading}
            >
              Unlock
            </Button>
          </form>
        </details>
      )}

      {/* Overwolf secret form — Overwolf can't sign in directly, so users
          unlock with a one-time secret copied from the web account page.
          Spell out the steps; "paste your secret" alone confused users. */}
      {isOverwolf && (
        <>
          <Separator />
          <div className="space-y-2">
            <p className="text-xs font-medium">Unlock manually (Overwolf)</p>
            <ol className="list-decimal space-y-1 pl-4 text-xs text-muted-foreground">
              <li>Become a Pro/Elite supporter on Patreon.</li>
              <li>
                Open the{" "}
                <ExternalAnchor
                  href={`${TH_GL_URL}/support-me/account`}
                  className="text-primary hover:underline"
                >
                  account page
                </ExternalAnchor>{" "}
                and authenticate with Patreon.
              </li>
              <li>
                Find this game and click{" "}
                <span className="font-medium">Copy Secret</span>.
              </li>
              <li>Paste it below and Unlock.</li>
            </ol>
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Paste your secret here"
                className="text-xs h-8"
              />
              <Button
                type="submit"
                size="sm"
                className="h-8 shrink-0"
                disabled={userId.length === 0 || loading}
              >
                Unlock
              </Button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

export function UserDialog() {
  const account = useAccountStore();

  return (
    <Dialog
      open={account.showUserDialog}
      onOpenChange={account.setShowUserDialog}
    >
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle className="text-base">
            {account.userId ? "Account" : "Sign In"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Manage your account and sign-in.
          </DialogDescription>
        </DialogHeader>
        {account.userId ? <AuthenticatedView /> : <UnauthenticatedView />}
      </DialogContent>
    </Dialog>
  );
}
